import { useEffect, useState } from "react";
import CustomForm from "../components/CustomForm";
import FormButtonCancel from "../components/FormButtonCancel";
import FormButtonSave from "../components/FormButtonSave";
import useFormHandler from "../hooks/useFormHandler";
import { getTenantSpecificUsers } from "../services/api-server/graphql";
import { updateTenantItem } from "../services/api-server/tenant_management";
import { tenantType } from "../types/tenant";
import { alertMessage } from "../utils/alert";
import Emitter from "../utils/emitter";
import { useTenantStore } from "../stores/tenantStore";

interface TenantDetailInterface {
	currentTenant: tenantType;
	setCurrentTenant: (tenant: tenantType) => void;
	onTenantSave?: () => void;
}

function TenantDetail({
	currentTenant,
	setCurrentTenant,
	onTenantSave,
}: TenantDetailInterface) {
	const { formRef, formReset, setFormReset, resetForm } = useFormHandler();
	const tenantList = useTenantStore((state) => state.tenantList);
	const setTenantList = useTenantStore((state) => state.setTenantList);

	const [tenantUsers, setTenantUsers] = useState<any>(null);

	useEffect(() => {
		if (currentTenant) {
			resetForm(currentTenant);
			customTenantUpdateHandler();
		}
	}, [currentTenant]);

	const customTenantUpdateHandler = async () => {
		try {
			Emitter.emit("loading", { name: "tenant-detail", isLoading: true });
			const tenantUsers: any = await getTenantSpecificUsers(currentTenant.uuid);
			setTenantUsers(tenantUsers);
			Emitter.emit("loading", { name: "tenant-detail", isLoading: false });
		} catch (error) {
			console.log("Error fetching `getTenantSpecificUsers`: ", error);
			Emitter.emit("loading", { name: "tenant-detail", isLoading: false });
		}
	};

	const handleFormSave = async (values: any) => {
		try {
			Emitter.emit("loading", { name: "tenant-detail", isLoading: true });
			const updatedItem: any = await updateTenantItem(
				currentTenant.uuid,
				values
			);
			resetForm(updatedItem);
			setCurrentTenant(updatedItem as tenantType);
			// Update the matching tenant in the tenant list store
			const updatedTenantList = tenantList.map((tenant: any) =>
				tenant.role_key === currentTenant.role_key
					? { ...tenant, ...updatedItem }
					: tenant
			);
			setTenantList(updatedTenantList);
			Emitter.emit("loading", { name: "tenant-detail", isLoading: false });
			alertMessage("success", "Tenant item has been successfully updated.");
			// Call onTenantSave callback if provided
			if (onTenantSave) {
				onTenantSave();
			}
		} catch (error) {
			console.log("Error updating tenant: ", error);
			Emitter.emit("loading", { name: "tenant-detail", isLoading: false });
		}
	};

	return (
		<>
			<div className="crud-tab-table-modal-container">
				<div className="main-container">
					<div className="generic-header">Configurations</div>
					<div
						className="generic-content"
						style={{
							display: "flex",
							padding: "20px 0px",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: "20px",
							flex: "1 1 0",
							alignSelf: "stretch",
							overflowY: "auto",
						}}
					>
						{currentTenant && (
							<CustomForm
								formRef={formRef}
								setFormReset={setFormReset}
								handleFormSave={handleFormSave}
								tabKey={"tenant"}
								formOption={"tenant-config-page"}
								activeRecord={currentTenant}
								dataOptions={{
									users: tenantUsers ?? [],
								}}
							/>
						)}
					</div>
					<div className="generic-footer">
						<FormButtonCancel
							form={formRef}
							handleFormCancel={() => resetForm(currentTenant)}
							formReset={formReset}
						/>
						<FormButtonSave
							form={formRef}
							handleFormSave={handleFormSave}
							formReset={formReset}
						/>
					</div>
				</div>
			</div>
		</>
	);
}

export default TenantDetail;
